<template>
  <div class="package-card w-full flex justify-center relative">
    <!-- Previous Package Button -->
    <!-- Package Card -->
    <div class="bg-gray-100 shadow-md rounded-lg hover:scale-105 transform transition duration-300 w-full max-w-2xl">
      <!-- Header Section -->
      <div class="pt-6 text-center">
        <h4 class="text-2xl font-bold text-gray-800">{{ pkg.name }}</h4>
        <p v-if="pkg.excerpt" class="text-gray-600">{{ pkg.excerpt }}</p>
      </div>

      <!-- Package Image -->
      <img v-if="pkg.image" :src="pkg.image.large" alt="Package Image" class="w-full my-4" />

      <!-- Price Section -->
      <div class="mt-4 flex flex-col items-center">
        <div class="label text-gray-500">{{ $translate('Package Price') }}</div>
        <div class="flex items-center">
          <div class="text-4xl text-blue-600 font-bold">{{ pkg.price.formatted }}</div>
          <div v-if="pkg.value > 0" class="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full ml-3">
            -{{ pkg.value }}%
          </div>
        </div>
        <div v-if="pkg.price.is_discounted" class="text-sm text-red-500 line-through mt-1">
          {{ pkg.price.original_price_formatted }}
        </div>
      </div>

      <!-- Show/Hide Package Details Button -->
      <div class="p-6">
        <button @click.stop="toggleShowEvents"
          class="w-full text-blue-600 font-semibold flex items-center justify-center">
          <font-awesome-icon :icon="showEvents ? 'chevron-up' : 'chevron-down'" class="mr-2" />
          {{ showEvents ? $translate('Hide Package Details') : $translate('Show Package Details (%s items)',
            [pkg.items.length]) }}
        </button>
      </div>

      <!-- Package Items List -->
      <div v-if="showEvents" class="bg-gray-50 border-t border-gray-200 items-area">
        <div class="p-6">
          <div class="text-sm font-semibold text-gray-500 mb-4">
            {{ $translate('Package Includes', [pkg.items.length]) }}:
          </div>

          <!-- Item List -->
          <div v-for="item in pkg.items" :key="item.id" class="flex items-center mb-6">
            <!-- Item Image -->
            <img :src="item.image.small" :alt="item.name"
              class="w-16 h-16 object-cover rounded-md border border-gray-300 mr-4 flex-shrink-0" />

            <!-- Item Details -->
            <div class="flex-1">
              <div class="text-base font-semibold text-gray-700">
                <template v-if="item.info && item.info.url">
                  <a :href="item.info.url" target="_blank" rel="noopener noreferrer"
                    class="text-blue-600 hover:underline">
                    {{ item.name }}
                  </a>
                </template>
                <template v-else>
                  {{ item.name }}
                </template>
                <small v-if="item.quantity > 1" class="text-sm text-gray-500">
                  ({{ item.quantity }}<span v-if="item.unit"> {{ item.unit }}</span>)
                </small>
              </div>
              <div class="text-sm text-gray-600 mt-1">{{ formattedDate(item) }}</div>
            </div>

            <!-- Price and Add to Cart Button -->
            <div class="flex-shrink-0 ml-4 flex items-center space-x-2 item-row">
              <!-- Price Display -->
              <span class="item-price text-base text-gray-700 font-medium">{{ item.price.formatted }}</span>
              <!-- Add to Cart Button -->
              <button @click.stop="selectItem(item)" class="item-button">
                {{ $translate('To cart') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Section (Footer) -->
      <div
        class="bottom bg-gray-300 rounded-b-lg flex justify-between items-center p-4 relative
             before:absolute before:content-[''] before:-left-3 before:top-0 before:w-6 before:h-6 before:bg-white before:rounded-full before:-translate-y-1/2
             after:absolute after:content-[''] after:-right-3 after:top-0 after:w-6 after:h-6 after:bg-white after:rounded-full after:-translate-y-1/2">
        <div class="barcode w-24 h-8 bg-repeat-y bg-barcode-pattern"></div>
        <button @click.stop="selectPackage"
          class="buy-ticket bg-blue-500 text-white font-semibold py-2 px-4 rounded-full hover:bg-blue-600">
          {{ $translate('Add Package to Cart') }}
        </button>
      </div>
    </div>
    <!-- Prev Package Button -->
    <button v-if="currentPackageIndex > 0" @click.stop="prevPackage"
      class="chevron-btn prev-package focus:outline-none text-2xl absolute -left-10 top-1/2 transform -translate-y-1/2 hover:text-blue-500 transition">
      <font-awesome-icon icon="chevron-left" />
    </button>

    <!-- Next Package Button -->
    <button v-if="currentPackageIndex < totalPackages - 1" @click.stop="nextPackage"
      class="chevron-btn next-package focus:outline-none text-2xl absolute -right-10 top-1/2 transform -translate-y-1/2 hover:text-blue-500 transition">
      <font-awesome-icon icon="chevron-right" />
    </button>
  </div>
</template>

<script>
export default {
  props: {
    pkg: {
      type: Object,
      required: true,
    },
    currentPackageIndex: {
      type: Number,
      required: true,
    },
    totalPackages: {
      type: Number,
      required: true,
    },
    nextPackage: {
      type: Function,
      required: true,
    },
    prevPackage: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showEvents: false, // Show package details by default
    };
  },
  computed: {

  },
  methods: {
    selectPackage() {
      this.$emit('select', this.pkg);
    },
    toggleShowEvents() {
      this.showEvents = !this.showEvents;
    },
    selectItem(item) {
      const itemType = item.type || 'event';  // Default to 'event' if item.type is not provided
      this.$emit('selectItem', { type: itemType, item });
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      if (isNaN(date)) {
        console.error('Invalid time format:', dateString);
        return 'Invalid Time';
      }
      const options = { hour: '2-digit', minute: '2-digit' };
      return date.toLocaleTimeString('cs-CZ', options);
    },
    /*  formattedDate(item) {
        if (!item.info.date) {
          return '';
        }
        // Parse the start date string properly
        const startDate = new Date(item.info.date.start_at);
  
        if (isNaN(startDate)) {
          console.error('Invalid date format:', item.info.date.start_at);
          return 'Invalid Date';
        }
  
        const dateOptions = { day: 'numeric', month: 'long' }; // Format with full month name
        const startDateStr = `${startDate.toLocaleDateString('cs-CZ', dateOptions)}`;
        const startTimeStr = this.formatTime(item.info.date.start_at);
  
        return `${startTimeStr}, ${startDateStr}`;
      },
  */
    formattedDate(item) {
      if (!item.info.date) {
        return '';
      }
      // Parse the date strings properly
      const startDate = new Date(item.info.date.start_at);
      const endDate = new Date(item.info.date.end_at);

      if (isNaN(startDate) || isNaN(endDate)) {
        console.error('Invalid date format:', item.info.date.start_at, item.info.date.end_at);
        return 'Invalid Date';
      }

      const dateOptions = { day: 'numeric', month: 'long' }; // Format with full month name
      const startDateStr = `${startDate.toLocaleDateString('cs-CZ', dateOptions)} ${this.formatTime(item.info.date.start_at)}`;
      const endDateStr = `${endDate.toLocaleDateString('cs-CZ', dateOptions)} ${this.formatTime(item.info.date.end_at)}`;

      if (this.isSingleDayEvent) {
        return `${startDateStr} - ${this.formatTime(item.info.date.end_at)}`;
      } else {
        return `${startDateStr} - ${endDateStr}`;
      }
    },


    isSingleDayEvent(item) {
      if (!item.info.date) {
        return '';
      }
      // Check if the start and end date are the same date (ignoring time)
      return new Date(item.info.date.start_at).toDateString() === new Date(item.info.date.end_at).toDateString();
    },
    formattedAddress(item) {
      if (!item.info.address) {
        return '';
      }
      //make sure only first two word from address will be useModel, if dash we add <br>
      return item.info.address.split(' ').slice(0, 4).join(' ');

    },
  },
};
</script>

<style scoped>
.package-card {
  max-width: 600px;
  position: relative;
}

/* Arrow Buttons */
.chevron-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #ccc;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chevron-btn:hover {
  background: #f0f0f0;
}

.bottom {
  border-top: 3px dashed #a9a9a9;
}

.barcode {
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAF4AAAABCAYAAABXChlMAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0LjAuOWwzfk4AAACPSURBVChTXVAJDsMgDOsrVpELiqb+/4c0DgStQ7JMYogNh2gdvg5VfXFCRIZaC6BOtnoNFpvaumNmwb/71Frrm8XvgYkker1/g9WzMOsohaOGNziRs5inDsAn8yEPengTapJ5bmdZ2Yv7VvfPN6AH2NJx7nOWPTf1/78hoqgxhzw3ZqYG1Dr/9ur3y8vMxgNZhcAUnR4xKgAAAABJRU5ErkJggg==');
}

.item-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.item-price {
  transition: opacity 0.3s ease;
}

.item-button {
  display: none;
  background-color: #2563eb;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  position: absolute;
  right: 0;
  width: 80px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.items-area {
  max-height: 200px;
  overflow-y: auto;
}

.item-row:hover .item-price {
  opacity: 0;
}

.item-row:hover .item-button {
  display: inline-block;
}

/* Adjust the spacing in the Price Section */
.price-section {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
}

.price-section .price {
  font-size: 2.5rem;
  color: #2563eb;
  font-weight: bold;
}

.price-section .percentage-badge {
  background-color: #dc2626;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  margin-left: 0.75rem;
}
</style>
